import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BookOpen, ArrowLeft, Calendar, CheckCircle2, Clock, User, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const activityTypeLabels = {
  reading: "Reading",
  reflection: "Reflection",
  task: "Task/Assignment",
  quiz: "Quiz",
  video: "Video",
  audio: "Audio"
};

const activityTypeColors = {
  reading: "bg-blue-100 text-blue-700",
  reflection: "bg-purple-100 text-purple-700",
  task: "bg-orange-100 text-orange-700",
  quiz: "bg-green-100 text-green-700",
  video: "bg-red-100 text-red-700",
  audio: "bg-yellow-100 text-yellow-700"
};

export default function CourseActivities({ user }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityProgress, setActivityProgress] = useState([]);
  const [activityDiscussions, setActivityDiscussions] = useState({});
  const [expandedActivities, setExpandedActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      // Load course details
      const courseRes = await fetch(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (courseRes.ok) {
        setCourse(await courseRes.json());
      }

      // Load activities
      const activitiesRes = await fetch(`${API}/courses/${courseId}/activities`, {
        headers: getAuthHeaders()
      });
      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData);
        
        // Load discussions for each activity
        const discussionsMap = {};
        for (const activity of activitiesData) {
          const discussionsRes = await fetch(`${API}/activities/${activity.activity_id}/discussions`, {
            headers: getAuthHeaders()
          });
          if (discussionsRes.ok) {
            const discussionsData = await discussionsRes.json();
            discussionsMap[activity.activity_id] = discussionsData.messages || [];
          }
        }
        setActivityDiscussions(discussionsMap);
      }

      // Load activity progress
      const progressRes = await fetch(`${API}/courses/${courseId}/activity-progress`, {
        headers: getAuthHeaders()
      });
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setActivityProgress(progressData.completed_activities || []);
      }
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const toggleActivityExpand = (activityId) => {
    setExpandedActivities(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  const groupActivitiesByLesson = () => {
    const grouped = {};
    activities.forEach(activity => {
      const lessonIndex = activity.lesson_index || 0;
      if (!grouped[lessonIndex]) {
        grouped[lessonIndex] = [];
      }
      grouped[lessonIndex].push(activity);
    });
    return grouped;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAEDCD] flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-[#D4A373] mx-auto mb-4 animate-pulse" />
          <p className="text-[#588157]">Loading activities...</p>
        </div>
      </div>
    );
  }

  const groupedActivities = groupActivitiesByLesson();
  const lessonIndices = Object.keys(groupedActivities).sort((a, b) => Number(a) - Number(b));

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/my-courses')}
            data-testid="back-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to My Courses</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#D4A373]" strokeWidth={1.5} />
            <h1 className="text-lg sm:text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Daily Activities
            </h1>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
        {/* Course Header */}
        {course && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
              {course.title}
            </h2>
            <p className="text-[#588157]">View and participate in daily activities for this course</p>
          </div>
        )}

        {activities.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <Calendar className="w-16 h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-[#344E41] mb-2">No Activities Yet</h3>
            <p className="text-[#588157]">The teacher hasn't added any daily activities for this course yet.</p>
          </Card>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {lessonIndices.map((lessonIndex) => {
              const lessonActivities = groupedActivities[lessonIndex];
              const lessonTitle = course?.content?.[lessonIndex]?.title || `Lesson ${Number(lessonIndex) + 1}`;
              
              return (
                <div key={lessonIndex}>
                  {/* Lesson Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#3A5A40] text-white rounded-full flex items-center justify-center font-bold">
                      {Number(lessonIndex) + 1}
                    </div>
                    <h3 className="text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                      {lessonTitle}
                    </h3>
                  </div>

                  {/* Activities for this lesson */}
                  <div className="space-y-4 ml-0 sm:ml-13">
                    {lessonActivities.map((activity) => {
                      const isCompleted = activityProgress.includes(activity.activity_id);
                      const isExpanded = expandedActivities[activity.activity_id];
                      const discussions = activityDiscussions[activity.activity_id] || [];
                      const myResponses = discussions.filter(d => d.user_id === user?.user_id);
                      const otherResponses = discussions.filter(d => d.user_id !== user?.user_id);

                      return (
                        <Card 
                          key={activity.activity_id}
                          className={`bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl overflow-hidden ${isCompleted ? 'border-l-4 border-l-[#3A5A40]' : ''}`}
                        >
                          {/* Activity Header */}
                          <div 
                            className="p-4 cursor-pointer hover:bg-[#F5F0DC] transition-colors"
                            onClick={() => toggleActivityExpand(activity.activity_id)}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className={`text-xs px-2 py-1 rounded-full ${activityTypeColors[activity.activity_type] || 'bg-gray-100 text-gray-700'}`}>
                                    {activityTypeLabels[activity.activity_type] || activity.activity_type}
                                  </span>
                                  <span className="text-xs text-[#A3B18A]">Day {activity.day_number}</span>
                                  {isCompleted && (
                                    <span className="flex items-center text-xs text-[#3A5A40]">
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Completed
                                    </span>
                                  )}
                                </div>
                                <h4 className="text-lg font-bold text-[#344E41]">{activity.title}</h4>
                                <p className="text-sm text-[#588157] line-clamp-2">{activity.description}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {discussions.length > 0 && (
                                  <span className="flex items-center text-xs text-[#588157] bg-[#E9EDC9] px-2 py-1 rounded-full">
                                    <MessageSquare className="w-3 h-3 mr-1" />
                                    {discussions.length}
                                  </span>
                                )}
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-[#A3B18A]" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-[#A3B18A]" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="border-t border-[#CCD5AE]/50 p-4 bg-white/50">
                              {/* Activity Content */}
                              {activity.content && (
                                <div className="mb-4 p-4 bg-[#FAEDCD] rounded-lg">
                                  <h5 className="text-sm font-bold text-[#344E41] mb-2">Activity Instructions:</h5>
                                  <p className="text-sm text-[#344E41] whitespace-pre-wrap">{activity.content}</p>
                                </div>
                              )}

                              {activity.url && (
                                <div className="mb-4">
                                  <a
                                    href={activity.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#3A5A40] underline text-sm hover:text-[#344E41]"
                                  >
                                    View Resource Link
                                  </a>
                                </div>
                              )}

                              {/* My Responses */}
                              {myResponses.length > 0 && (
                                <div className="mb-4">
                                  <h5 className="text-sm font-bold text-[#3A5A40] mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    My Responses ({myResponses.length})
                                  </h5>
                                  <div className="space-y-2">
                                    {myResponses.map((response, idx) => (
                                      <div key={idx} className="p-3 bg-[#E9EDC9] rounded-lg border-l-4 border-l-[#3A5A40]">
                                        <p className="text-sm text-[#344E41]">{response.message}</p>
                                        <p className="text-xs text-[#A3B18A] mt-1">
                                          <Clock className="w-3 h-3 inline mr-1" />
                                          {formatDate(response.created_at)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Other People's Responses */}
                              {otherResponses.length > 0 && (
                                <div className="mb-4">
                                  <h5 className="text-sm font-bold text-[#588157] mb-3 flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4" />
                                    Other Responses ({otherResponses.length})
                                  </h5>
                                  <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {otherResponses.map((response, idx) => (
                                      <div key={idx} className="p-3 bg-white rounded-lg border border-[#CCD5AE]/50">
                                        <div className="flex items-center gap-2 mb-1">
                                          <div className="w-6 h-6 bg-[#D4A373] text-white rounded-full flex items-center justify-center text-xs font-bold">
                                            {response.user_name?.[0] || '?'}
                                          </div>
                                          <span className="text-sm font-medium text-[#344E41]">{response.user_name || 'Student'}</span>
                                          <span className="text-xs text-[#A3B18A]">
                                            {response.user_role === 'teacher' && '(Teacher)'}
                                          </span>
                                        </div>
                                        <p className="text-sm text-[#344E41] ml-8">{response.message}</p>
                                        <p className="text-xs text-[#A3B18A] mt-1 ml-8">
                                          <Clock className="w-3 h-3 inline mr-1" />
                                          {formatDate(response.created_at)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* No responses yet */}
                              {discussions.length === 0 && (
                                <p className="text-sm text-[#A3B18A] italic">No responses yet. Be the first to respond!</p>
                              )}

                              {/* Go to Activity Button */}
                              <Button
                                onClick={() => navigate(`/courses/${courseId}/view`)}
                                className="mt-4 bg-[#3A5A40] text-white hover:bg-[#344E41]"
                              >
                                Go to Course to Respond
                              </Button>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
